import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CancelJobDialog = ({ open, onOpenChange, deliveryOrder, onCancelled }) => {
  const [charges, setCharges] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !deliveryOrder) return;
    const items = (deliveryOrder.line_items || []).map((it) => {
      const price = Math.round(((it.unit_price || 0) * 0.5) * 100) / 100;
      const qty = it.quantity || 1;
      const hours = it.hours || 0;
      return {
        description: `CANCELLATION FEE (Half Trip) — ${it.description || ''}`,
        quantity: qty,
        hours,
        lorry_no: it.lorry_no || '',
        unit_price: price,
        amount: Math.round((hours > 0 ? qty * hours * price : qty * price) * 100) / 100,
      };
    });
    setCharges(items.length ? items : [{ description: 'CANCELLATION FEE', quantity: 1, hours: 0, unit_price: 0, amount: 0 }]);
  }, [open, deliveryOrder]);

  const update = (idx, field, value) => {
    const next = [...charges];
    next[idx][field] = value;
    if (field === 'quantity' || field === 'hours' || field === 'unit_price') {
      const qty = parseFloat(next[idx].quantity) || 0;
      const hours = parseFloat(next[idx].hours) || 0;
      const price = parseFloat(next[idx].unit_price) || 0;
      next[idx].amount = Math.round((hours > 0 ? qty * hours * price : qty * price) * 100) / 100;
    }
    setCharges(next);
  };

  const total = charges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/delivery-orders/${deliveryOrder.id}/cancel`, {
        charges: charges.map((c) => ({ ...c, amount: parseFloat(c.amount) || 0 })),
      });
      toast.success(res.data.message);
      onOpenChange(false);
      onCancelled?.(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to cancel job'));
    } finally {
      setBusy(false);
    }
  };

  const cancelWithoutCharges = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/delivery-orders/${deliveryOrder.id}/cancel`, { charges: [] });
      toast.success(res.data.message);
      onOpenChange(false);
      onCancelled?.(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to cancel job'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <XCircle size={20} /> Cancel Job {deliveryOrder?.document_number}
          </DialogTitle>
          <DialogDescription>
            Review and edit the cancellation charges below. They will be billed on the linked invoice (or a new one if none exists).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {charges.map((c, idx) => (
            <div key={idx} className="p-3 bg-gray-50 rounded-lg space-y-2">
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={c.description} onChange={(e) => update(idx, 'description', e.target.value)}
                       data-testid={`cancel-charge-desc-${idx}`} />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" min="0" value={c.quantity} onChange={(e) => update(idx, 'quantity', e.target.value)}
                         data-testid={`cancel-charge-qty-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs">Hours</Label>
                  <Input type="number" min="0" step="0.5" value={c.hours} onChange={(e) => update(idx, 'hours', e.target.value)}
                         data-testid={`cancel-charge-hours-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs">Unit Price ($)</Label>
                  <Input type="number" min="0" step="0.01" value={c.unit_price} onChange={(e) => update(idx, 'unit_price', e.target.value)}
                         data-testid={`cancel-charge-price-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs">Amount ($)</Label>
                  <Input type="number" min="0" step="0.01" value={c.amount} onChange={(e) => update(idx, 'amount', e.target.value)}
                         data-testid={`cancel-charge-amount-${idx}`} />
                </div>
              </div>
              <Button type="button" variant="outline" size="sm" className="text-red-600"
                      onClick={() => setCharges(charges.filter((_, i) => i !== idx))}
                      data-testid={`cancel-charge-remove-${idx}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm"
                  onClick={() => setCharges([...charges, { description: '', quantity: 1, hours: 0, unit_price: 0, amount: 0 }])}
                  data-testid="cancel-charge-add-btn">
            <Plus size={14} className="mr-1" /> Add Charge
          </Button>
          <div className="text-right font-bold" data-testid="cancel-charges-total">
            Total Cancellation Charges: ${total.toFixed(2)}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={cancelWithoutCharges} disabled={busy} data-testid="cancel-no-charge-btn">
            Cancel Job — No Charge
          </Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submit} disabled={busy}
                  data-testid="confirm-cancel-job-btn">
            {busy ? 'Cancelling...' : `Cancel Job & Bill $${total.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelJobDialog;
